import { useState } from "react";
import { Search, Check, Copy } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { iconsList, IconItem } from "@/data/brandGuide";

export const IconGrid = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [copiedIcon, setCopiedIcon] = useState<string | null>(null);

  const categories = Array.from(new Set(iconsList.map(icon => icon.category)));

  const filteredIcons = iconsList.filter(icon => {
    const matchesSearch = icon.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || icon.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const copyIconName = (iconName: string) => {
    const importStatement = `import { ${iconName} } from "lucide-react";`;
    navigator.clipboard.writeText(importStatement);
    setCopiedIcon(iconName);
    toast.success("Código de importación copiado");
    setTimeout(() => setCopiedIcon(null), 2000);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Buscar iconos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant={selectedCategory === null ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedCategory(null)}
        >
          Todos ({iconsList.length})
        </Button>
        {categories.map(category => (
          <Button
            key={category}
            variant={selectedCategory === category ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedCategory(category)}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)} (
            {iconsList.filter(i => i.category === category).length})
          </Button>
        ))}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {filteredIcons.map((icon) => {
          const IconComponent = icon.component;
          return (
            <Card
              key={icon.name}
              className="p-4 hover:shadow-lg transition-all duration-300 cursor-pointer group relative"
              onClick={() => copyIconName(icon.name)}
            >
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 rounded-lg bg-muted group-hover:bg-primary/10 transition-colors">
                  <IconComponent className="h-6 w-6 text-foreground group-hover:text-primary transition-colors" />
                </div>
                <div className="text-center w-full">
                  <p className="text-xs font-medium truncate">{icon.name}</p>
                  <Badge variant="outline" className="text-[10px] mt-1">
                    {icon.category}
                  </Badge>
                </div>
              </div>
              {copiedIcon === icon.name && (
                <div className="absolute inset-0 bg-viable-green/10 rounded-lg flex items-center justify-center">
                  <Check className="h-6 w-6 text-viable-green" />
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {filteredIcons.length === 0 && (
        <div className="text-center py-12">
          <p className="text-muted-foreground">No se encontraron iconos</p>
        </div>
      )}
    </div>
  );
};
